/**
 * <ship-cutoff>: same-day shipping countdown (see snippets/ship-cutoff.liquid).
 * All date maths happens on Pacific wall-clock time (America/Vancouver, DST aware),
 * whatever the shopper's own time zone.
 */
(() => {
  if (customElements.get('ship-cutoff')) return;

  const TZ = 'America/Vancouver';
  const DAY = 86400000;
  const SHOW_WITHIN = 12 * 3600000; // countdown only appears within 12 hours of the next cutoff
  const pad = (n) => String(n).padStart(2, '0');

  // Pacific wall-clock time for an instant, expressed as a UTC timestamp (so UTC getters read PT fields).
  const ptWall = (date) => {
    const parts = {};
    new Intl.DateTimeFormat('en-CA', {
      timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    }).formatToParts(date).forEach((p) => { parts[p.type] = p.value; });
    return Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
  };

  // Real instant for a Pacific wall-clock timestamp (one correction step handles DST changes).
  const wallToInstant = (wall) => {
    let guess = wall - (ptWall(new Date(wall)) - wall);
    guess += wall - ptWall(new Date(guess));
    return guess;
  };

  const ymd = (wall) => {
    const d = new Date(wall);
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  };

  // ---- BC statutory holidays, calculated for any year (no yearly list to maintain) ----
  const day = (y, m, d) => Date.UTC(y, m - 1, d);
  // nth weekday of a month (weekday 0 Sun … 6 Sat; n = 1, 2, 3…)
  const nthWeekday = (y, m, weekday, n) => {
    const first = new Date(day(y, m, 1)).getUTCDay();
    return day(y, m, 1 + ((weekday - first + 7) % 7) + (n - 1) * 7);
  };
  // Easter Sunday (Anonymous Gregorian algorithm)
  const easter = (y) => {
    const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4;
    const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31), dayOfMonth = ((h + l - 7 * m + 114) % 31) + 1;
    return day(y, month, dayOfMonth);
  };
  // fixed-date holiday on a weekend: the following Monday is the day off
  const observed = (wall) => {
    const wd = new Date(wall).getUTCDay();
    return wd === 6 ? wall + 2 * DAY : wd === 0 ? wall + DAY : wall;
  };
  const holidayCache = new Map();
  const bcHolidays = (y) => {
    if (holidayCache.has(y)) return holidayCache.get(y);
    const fixed = [day(y, 1, 1), day(y, 7, 1), day(y, 9, 30), day(y, 11, 11), day(y, 12, 25)]; // New Year, Canada Day, Truth & Reconciliation, Remembrance, Christmas
    const set = new Set();
    fixed.forEach((w) => { set.add(ymd(w)); set.add(ymd(observed(w))); });
    set.add(ymd(nthWeekday(y, 2, 1, 3)));                 // Family Day: 3rd Monday of February
    set.add(ymd(easter(y) - 2 * DAY));                    // Good Friday
    const may24 = day(y, 5, 24);                          // Victoria Day: Monday on or before May 24
    set.add(ymd(may24 - ((new Date(may24).getUTCDay() + 6) % 7) * DAY));
    set.add(ymd(nthWeekday(y, 8, 1, 1)));                 // BC Day: 1st Monday of August
    set.add(ymd(nthWeekday(y, 9, 1, 1)));                 // Labour Day: 1st Monday of September
    set.add(ymd(nthWeekday(y, 10, 1, 2)));                // Thanksgiving: 2nd Monday of October
    holidayCache.set(y, set);
    return set;
  };

  class ShipCutoff extends HTMLElement {
    connectedCallback() {
      this.cutoffHour = Number(this.dataset.cutoffHour) || 12;
      this.holidays = new Set((this.dataset.holidays || '').split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean));
      this.autoHolidays = this.dataset.autoHolidays !== 'false';
      const units = (this.dataset.units || 'd,h,m,s').split(',');
      this.units = { d: units[0] || 'd', h: units[1] || 'h', m: units[2] || 'm', s: units[3] || 's' };
      this.textEl = this.querySelector('[data-ship-cutoff-text]');
      this.locale = this.dataset.locale || document.documentElement.lang || 'en';
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    isShipDay(dayWall) {
      const weekday = new Date(dayWall).getUTCDay(); // 0 Sun … 6 Sat
      if (weekday < 1 || weekday > 5) return false;
      const key = ymd(dayWall);
      if (this.holidays.has(key)) return false;
      if (this.autoHolidays && bcHolidays(new Date(dayWall).getUTCFullYear()).has(key)) return false;
      return true;
    }

    // Next cutoff instant + which label to use ('today' | 'tomorrow' | weekday name).
    nextCutoff(now) {
      const nowWall = ptWall(now);
      const todayWall = Date.UTC(new Date(nowWall).getUTCFullYear(), new Date(nowWall).getUTCMonth(), new Date(nowWall).getUTCDate());
      for (let i = 0; i < 21; i++) {
        const dayWall = todayWall + i * DAY;
        const cutoffWall = dayWall + this.cutoffHour * 3600000;
        if (!this.isShipDay(dayWall) || cutoffWall <= nowWall) continue;
        let when = 'day';
        if (i === 0) when = 'today';
        else if (i === 1) when = 'tomorrow';
        const dayName = new Intl.DateTimeFormat(this.locale, { weekday: 'long', timeZone: 'UTC' }).format(new Date(dayWall));
        return { at: wallToInstant(cutoffWall), when, dayName };
      }
      return null;
    }

    format(ms) {
      const total = Math.max(0, Math.floor(ms / 1000));
      const d = Math.floor(total / 86400);
      const h = Math.floor((total % 86400) / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      const u = this.units;
      if (d > 0) return `${d}${u.d} ${h}${u.h}`;
      if (h > 0) return `${h}${u.h} ${pad(m)}${u.m} ${pad(s)}${u.s}`;
      return `${m}${u.m} ${pad(s)}${u.s}`;
    }

    tick() {
      const now = new Date();
      const next = this.nextCutoff(now);
      // Only show the countdown in the last 12 hours before a cutoff; hidden the rest of the time.
      if (!next || !this.textEl || next.at - now.getTime() > SHOW_WITHIN) { this.hidden = true; return; }
      const template = next.when === 'today' ? this.dataset.labelToday
        : next.when === 'tomorrow' ? this.dataset.labelTomorrow
        : this.dataset.labelDay;
      const html = (template || '')
        .replace(/\[time\]/g, this.format(next.at - now.getTime()))
        .replace(/\[day\]/g, next.dayName);
      if (this.textEl.innerHTML !== html) this.textEl.innerHTML = html;
      this.hidden = false;
    }
  }

  customElements.define('ship-cutoff', ShipCutoff);
  window.RiversolShipCutoff = { bcHolidays: (y) => [...bcHolidays(y)].sort() };
})();
